import {
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

// LOT0F7_PRP_UI_CONTINUITY_V1

const PRP_UI_PREFIX =
  "forfeo:prp-ui:v1:";

const PRP_UI_VERSION =
  1;

const PRP_UI_TTL_MS =
  12
  * 60
  * 60
  * 1000;

type StoredEnvelope<T> = {
  version: number;
  saved_at: number;
  value: T;
};

const storageKey = (
  key: string,
): string =>
  `${PRP_UI_PREFIX}${key}`;

const canUseSessionStorage =
  (): boolean =>
    typeof window !== "undefined"
    && typeof window.sessionStorage !== "undefined";

export const readPrpUiState = <T,>(
  key: string,
  fallback: T,
  validate?: (
    value: unknown,
  ) => boolean,
): T => {
  if (!canUseSessionStorage()) {
    return fallback;
  }

  const fullKey =
    storageKey(key);

  try {
    const raw =
      window.sessionStorage
        .getItem(fullKey);

    if (!raw) {
      return fallback;
    }

    const parsed =
      JSON.parse(raw) as StoredEnvelope<unknown>;

    if (
      !parsed
      || parsed.version !== PRP_UI_VERSION
      || typeof parsed.saved_at !== "number"
      || Date.now() - parsed.saved_at > PRP_UI_TTL_MS
      || (
        validate
        && !validate(parsed.value)
      )
    ) {
      window.sessionStorage
        .removeItem(fullKey);

      return fallback;
    }

    return parsed.value as T;
  } catch {
    try {
      window.sessionStorage
        .removeItem(fullKey);
    } catch {
      // Browser storage can be unavailable.
    }

    return fallback;
  }
};

export const writePrpUiState = <T,>(
  key: string,
  value: T,
): void => {
  if (!canUseSessionStorage()) {
    return;
  }

  const envelope:
    StoredEnvelope<T> = {
      version:
        PRP_UI_VERSION,
      saved_at:
        Date.now(),
      value,
    };

  try {
    window.sessionStorage
      .setItem(
        storageKey(key),
        JSON.stringify(envelope),
      );
  } catch {
    // UI continuity must never break the workspace.
  }
};

export const usePrpSessionState = <T,>(
  key: string,
  fallback: T,
  validate?: (
    value: unknown,
  ) => boolean,
): [
  T,
  Dispatch<SetStateAction<T>>,
] => {
  const [value, setValue] =
    useState<T>(
      () =>
        readPrpUiState(
          key,
          fallback,
          validate,
        ),
    );

  useEffect(
    () => {
      writePrpUiState(
        key,
        value,
      );
    },
    [
      key,
      value,
    ],
  );

  return [
    value,
    setValue,
  ];
};

export const usePrpScrollRestoration = (
  key: string,
  enabled = true,
): void => {
  useEffect(
    () => {
      if (
        typeof window === "undefined"
        || !enabled
      ) {
        return;
      }

      const savedY =
        readPrpUiState<number>(
          key,
          0,
          (value) =>
            typeof value === "number"
            && Number.isFinite(value)
            && value >= 0,
        );

      const restoreTimer =
        window.setTimeout(
          () => {
            window.scrollTo({
              top:
                savedY,
              left:
                0,
              behavior:
                "auto",
            });
          },
          0,
        );

      let saveTimer:
        number | null =
        null;

      const saveScroll =
        () => {
          if (saveTimer !== null) {
            window.clearTimeout(
              saveTimer,
            );
          }

          saveTimer =
            window.setTimeout(
              () => {
                writePrpUiState(
                  key,
                  Math.max(
                    0,
                    window.scrollY,
                  ),
                );
              },
              120,
            );
        };

      window.addEventListener(
        "scroll",
        saveScroll,
        {
          passive:
            true,
        },
      );

      return () => {
        window.clearTimeout(
          restoreTimer,
        );

        if (saveTimer !== null) {
          window.clearTimeout(
            saveTimer,
          );
        }

        writePrpUiState(
          key,
          Math.max(
            0,
            window.scrollY,
          ),
        );

        window.removeEventListener(
          "scroll",
          saveScroll,
        );
      };
    },
    [
      enabled,
      key,
    ],
  );
};

export const clearPrpUiContinuity =
  (): void => {
    if (!canUseSessionStorage()) {
      return;
    }

    try {
      for (
        let index =
          window.sessionStorage.length - 1;
        index >= 0;
        index -= 1
      ) {
        const key =
          window.sessionStorage.key(index);

        if (
          key?.startsWith(
            PRP_UI_PREFIX,
          )
        ) {
          window.sessionStorage
            .removeItem(key);
        }
      }
    } catch {
      // Sign-out cleanup is best-effort and never blocks logout.
    }
  };
